import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Search, UserCog } from "lucide-react";
import type { ManagedUser, ManagedUserListResponse } from "../../shared/types";
import { api, jsonBody } from "../lib/api";
import { accountRoleLabel, date, number } from "../lib/format";
import { useSession } from "../context/session";
import { PageHeader } from "../components/app-shell";
import { Button, Empty, ErrorState, Loading, Tag, useToast } from "../components/ui";

export function UsersPage() {
  const { user, config } = useSession();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const query = useQuery({
    queryKey: ["users", page, search],
    queryFn: () => {
      const params = new URLSearchParams({ page: String(page), pageSize: "20" });
      if (search.trim()) params.set("q", search.trim());
      return api<ManagedUserListResponse>(`/api/users?${params.toString()}`);
    },
  });
  const mutation = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: { role?: "admin" | "user"; tier?: "standard" | "vip"; status?: "active" | "suspended" } }) =>
      api<ManagedUser>(`/api/users/${id}`, { method: "PATCH", ...jsonBody(patch) }),
    async onSuccess() {
      toast.push("用户权限已更新");
      await queryClient.invalidateQueries({ queryKey: ["users"] });
    },
    onError: toast.fromError,
  });
  return (
    <div className="page users-page">
      <PageHeader eyebrow="IDENTITY · 用户" title="账号与权限" body="查看每个账号拥有的链接与总点击；停用账号会同时撤销其现有会话。" actions={<Tag tone="neutral">{query.data?.pagination.total ?? "—"} USERS</Tag>} />
      <div className="mail-admin-toolbar users-toolbar"><span className="eyebrow">PAGED DIRECTORY · 每页 20</span><label className="mail-admin-search"><Search size={14} /><input value={search} onChange={(event) => { setSearch(event.target.value); setPage(1); }} placeholder="搜索注册邮箱" /></label></div>
      {query.isLoading ? <Loading label="读取用户" /> : query.error ? <ErrorState error={query.error} retry={() => void query.refetch()} /> : query.data?.items.length ? (
        <><section className="users-table-wrap">
          <table className="data-table users-table">
            <thead><tr><th>账号 · EMAIL</th><th>角色</th><th>状态</th><th className="numeric">短链</th><th className="numeric">总点击</th><th>上次登录</th><th>创建</th></tr></thead>
            <tbody>{query.data.items.map((item) => {
              const immutable = item.role === "super_admin" || item.id === user?.id;
              return <tr key={item.id}>
                <td data-label="账号"><div className="user-cell"><span className="avatar">{item.email[0]?.toUpperCase()}</span><div><strong>{item.email}</strong><span>{item.id.slice(0, 8)}</span></div></div></td>
                <td data-label="角色">{item.role === "super_admin" ? <Tag tone="dark">{accountRoleLabel(item.role, item.tier)}</Tag> : <select className="table-select" value={item.role === "admin" ? "admin" : item.tier === "vip" ? "vip" : "user"} disabled={immutable || user?.role !== "super_admin"} onChange={(event) => {
                  const access = event.target.value as "user" | "vip" | "admin";
                  mutation.mutate({
                    id: item.id,
                    patch: access === "admin"
                      ? { role: "admin", tier: "standard" }
                      : { role: "user", tier: access === "vip" ? "vip" : "standard" },
                  });
                }}><option value="user">普通用户</option><option value="vip">VIP 用户</option><option value="admin">管理员</option></select>}</td>
                <td data-label="状态"><select className="table-select" value={item.status} disabled={immutable} onChange={(event) => mutation.mutate({ id: item.id, patch: { status: event.target.value as "active" | "suspended" } })}><option value="active">生效</option><option value="suspended">停用</option></select></td>
                <td className="numeric" data-label="短链">{number(item.linkCount)}</td>
                <td className="numeric" data-label="总点击">{number(item.totalClicks)}</td>
                <td data-label="上次登录">{date(item.lastLoginAt, true)}</td>
                <td data-label="创建">{date(item.createdAt)}</td>
              </tr>;
            })}</tbody>
          </table>
        </section><footer className="pagination"><span>第 {query.data.pagination.page} / {query.data.pagination.totalPages} 页 · 共 {query.data.pagination.total} 个用户</span><div><Button disabled={page <= 1} onClick={() => setPage((value) => value - 1)}>← 上一页</Button><Button disabled={page >= query.data.pagination.totalPages} onClick={() => setPage((value) => value + 1)}>下一页 →</Button></div></footer></>
      ) : <Empty title="没有用户" body="开放注册后，新账号会出现在这里。" />}
      <footer className="policy-note"><UserCog size={17} /><p><strong>分层授权。</strong> VIP 可使用更多邮箱域名、地址额度和正好 {config?.vipSlugLength ?? 5} 位的自定义短号，但仍看不到任何管理菜单；只有超级管理员可以授予 VIP 或管理员权限。</p></footer>
    </div>
  );
}
